<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import TaskRow from 'dashboard/routes/dashboard/tasks/TaskRow.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Object,
    default: () => ({}),
  },
  unassigned: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['move', 'add', 'open']);

const { t } = useI18n();

const UNASSIGNED_KEY = 'unassigned';

// Per-column inline add
const addingKey = ref(null);
const addTitle = ref('');
const addInput = ref(null);

// Ordered stage columns + a trailing Unassigned column. Each column carries the
// stageId it maps to (null for the synthetic unassigned column) and its items.
const boardColumns = computed(() => {
  const orderedStages = [...props.stages].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );
  const stageColumns = orderedStages.map(stage => ({
    key: String(stage.id),
    stageId: stage.id,
    label: stage.name,
    wipLimit: stage.wip_limit,
    statusCategory: stage.status_category,
    // shallow-copy so vuedraggable never mutates the parent's prop arrays
    items: [
      ...(props.columns[stage.id] || props.columns[String(stage.id)] || []),
    ],
  }));
  stageColumns.push({
    key: UNASSIGNED_KEY,
    stageId: null,
    label: t('PROJECTS.BOARD.UNASSIGNED'),
    wipLimit: null,
    statusCategory: null,
    items: [...(props.unassigned || [])],
  });
  return stageColumns;
});

const isOverLimit = column =>
  column.wipLimit != null &&
  column.wipLimit > 0 &&
  column.items.length > column.wipLimit;

// vuedraggable fires `change` with { added: { element, newIndex } } on the
// destination column when a card lands. We translate that into a move event.
const onColumnChange = (column, evt) => {
  if (!evt.added) return;
  const task = evt.added.element;
  emit('move', {
    taskId: task.id,
    toStageId: column.stageId,
    toIndex: evt.added.newIndex,
  });
};

const focusAddInput = () => {
  const el = Array.isArray(addInput.value) ? addInput.value[0] : addInput.value;
  el?.focus?.();
};

const startAdd = key => {
  addingKey.value = key;
  addTitle.value = '';
  nextTick(focusAddInput);
};

const cancelAdd = () => {
  addingKey.value = null;
  addTitle.value = '';
};

const submitAdd = column => {
  const title = addTitle.value.trim();
  if (!title) return;
  emit('add', { stageId: column.stageId, title });
  addTitle.value = '';
  nextTick(focusAddInput);
};

const onOpen = task => emit('open', task);
</script>

<template>
  <div class="relative flex flex-1 gap-4 px-6 py-4 overflow-x-auto">
    <div
      v-if="loading"
      class="absolute inset-0 z-10 flex items-center justify-center bg-n-background/60"
    >
      <span class="i-lucide-loader-circle animate-spin text-n-slate-10" />
    </div>

    <section
      v-for="column in boardColumns"
      :key="column.key"
      class="flex flex-col w-72 max-h-full shrink-0 rounded-xl bg-n-alpha-1"
    >
      <div class="flex items-center gap-2 px-3 py-2.5 shrink-0">
        <span
          class="text-xs font-semibold tracking-wide uppercase truncate text-n-slate-12"
        >
          {{ column.label }}
        </span>
        <span class="text-xs text-n-slate-10">{{ column.items.length }}</span>
        <span
          v-if="column.wipLimit != null && column.wipLimit > 0"
          class="ml-auto text-xs font-medium tabular-nums"
          :class="isOverLimit(column) ? 'text-n-ruby-9' : 'text-n-slate-10'"
        >
          {{ column.items.length }}/{{ column.wipLimit }}
        </span>
      </div>

      <Draggable
        :list="column.items"
        :group="{ name: 'project-tasks' }"
        item-key="id"
        :animation="150"
        ghost-class="opacity-40"
        class="flex flex-col flex-1 gap-2 px-2 pb-2 overflow-y-auto min-h-[40px]"
        @change="onColumnChange(column, $event)"
      >
        <template #item="{ element }">
          <TaskRow :task="element" layout="card" @open="onOpen" />
        </template>
      </Draggable>

      <!-- Inline add -->
      <div class="px-2 pb-2 shrink-0">
        <input
          v-if="addingKey === column.key"
          ref="addInput"
          v-model="addTitle"
          type="text"
          :placeholder="t('PROJECTS.BOARD.ADD_TASK_PROMPT')"
          class="w-full px-2 py-1.5 text-sm border-none rounded-lg reset-base bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand outline-1 outline outline-transparent"
          @keyup.enter="submitAdd(column)"
          @keyup.esc="cancelAdd"
        />
        <Button
          v-else
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('PROJECTS.BOARD.ADD_TASK')"
          class="justify-start w-full"
          @click="startAdd(column.key)"
        />
      </div>
    </section>
  </div>
</template>
